import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useGameUpdate, GameUpdateInput } from "@/graphql/hooks/useGameUpdate"
import { useToast } from "@/hooks/use-toast"
import { AvatarUpload } from "@/components/ui/avatar-upload"
import type { Game } from "@/graphql/types"

interface GameEditModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  game: Game | null
  onSuccess: () => void
}

export function GameEditModal({ open, onOpenChange, game, onSuccess }: GameEditModalProps) {
  const { updateGame, loading } = useGameUpdate()
  const { toast } = useToast()
  const [formData, setFormData] = useState<GameUpdateInput>({
    id: 0,
    name: "",
    description: "",
    isActive: 1,
    urlImage: "",
  })

  useEffect(() => {
    if (game) {
      setFormData({
        id: game.id,
        name: game.name,
        description: game.description || "",
        isActive: game.isActive,
        urlImage: game.urlImage,
      })
    }
  }, [game])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!game) return

    try {
      const result = await updateGame(formData)
      if (result?.status?.success) {
        toast({
          title: "Juego actualizado",
          description: "El juego se ha actualizado correctamente",
          variant: "success",
        })
        onSuccess()
        onOpenChange(false)
      } else {
        throw new Error(result?.status?.detail || "Error desconocido")
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Error al actualizar el juego",
        variant: "destructive",
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[625px]">
        <DialogHeader>
          <DialogTitle>Editar Juego</DialogTitle>
          <DialogDescription>Modifica la información del juego</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex justify-center mb-4">
            <AvatarUpload
              src={formData.urlImage}
              name={formData.name}
              personId={formData.id}
              gameId={formData.id}
              type="game.image"
              className="mx-auto w-full h-full"
              cb={() => {
                onSuccess()
                onOpenChange(false)
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Nombre</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="isActive"
              checked={!!formData.isActive}
              onCheckedChange={(checked) => setFormData({ ...formData, isActive: Number(checked) })}
            />
            <Label htmlFor="isActive">Activo</Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
